import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { generatePath, useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import { useRootStore } from 'stores/RootStore'
import { pluralize } from 'utils'
import spinnerImg from './spinner.png'
import { useTutorialModalState } from 'hooks/useTutorialModalState'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCardDismissal } from 'hooks/queries/cardDismissals/useCardDismissal'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { QuestionnairePanel } from './QuestionnairePanel'
import Styles from './GenerationStatusNotification.module.scss'

const MINIMIZE_STORAGE_KEY = (workspaceId: number | string) => `generation_questionnaire_minimized:${workspaceId}`

export const GenerationStatusNotification = observer(() => {
  const { contentGeneration: store } = useRootStore()
  const { isTutorialModalOpen } = useTutorialModalState()
  const { isMobile } = useResponsiveDesign()
  const isQuestionnaireEnabled = useFlipperFlag('generation_questionnaire')
  const { data: dismissalData, dismissCard } = useCardDismissal('generation_questionnaire')
  const workspace = useCurrentWorkspace()
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()

  // Added to avoid showing for existing users
  const isWorkspaceCreatedAfterV2Launch =
    !!workspace?.created_at && dayjs(workspace.created_at).isAfter(dayjs('2026-03-22').startOf('day'))

  const shouldShowQuestionnaire =
    isQuestionnaireEnabled &&
    isWorkspaceCreatedAfterV2Launch &&
    !isMobile &&
    store.isActive &&
    !dismissalData?.is_dismissed

  useEffect(() => {
    if (shouldShowQuestionnaire) {
      store.setShowQuestionnaire(true)
    }
  }, [shouldShowQuestionnaire, store])

  const questionnaireWasShownRef = useRef(false)
  useEffect(() => {
    if (store.showQuestionnaire) {
      questionnaireWasShownRef.current = true
    }
  }, [store.showQuestionnaire])

  useEffect(() => {
    if (store.isGenerationDone && questionnaireWasShownRef.current) {
      questionnaireWasShownRef.current = false
      dismissCard()
      localStorage.removeItem(MINIMIZE_STORAGE_KEY(workspaceId))
    }
  }, [store.isGenerationDone, dismissCard, workspaceId])

  useEffect(() => {
    return history.listen(() => {
      if (store.showQuestionnaire && !store.isQuestionnaireMinimized) {
        store.setQuestionnaireMinimized(true)
      }
    })
  }, [history, store])

  useEffect(() => {
    if (store.showQuestionnaire) {
      const persisted = localStorage.getItem(MINIMIZE_STORAGE_KEY(workspaceId))
      if (persisted !== null) {
        store.setQuestionnaireMinimized(persisted === 'true')
      }
    }
  }, [store.showQuestionnaire, workspaceId, store])

  useEffect(() => {
    if (store.showQuestionnaire) {
      localStorage.setItem(MINIMIZE_STORAGE_KEY(workspaceId), String(store.isQuestionnaireMinimized))
    }
  }, [store.isQuestionnaireMinimized, store.showQuestionnaire, workspaceId, store])

  if (!store.isVisible || isTutorialModalOpen || store.shouldYieldToFooter) return null

  if (store.isGenerationDone) {
    return <FinishedBar />
  }

  if (store.showQuestionnaire) {
    return (
      <>
        <div
          className={cx(Styles.contentOverlay, {
            [Styles.contentOverlayVisible]: !store.isQuestionnaireMinimized,
          })}
        />
        <div
          className={cx(Styles.barWithPanel, {
            [Styles.minimized]: store.isQuestionnaireMinimized,
          })}
        >
          <GeneratingBar />
          <QuestionnairePanel />
        </div>
      </>
    )
  }

  return <GeneratingBar />
})

const GeneratingBar = observer(() => {
  const { contentGeneration: store } = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const elapsed = useElapsedTime(store.startedAt)

  const displayTotal = store.hasScheduleData ? store.totalCount : store.numberOfNewPosts
  const showCounter = displayTotal > 0

  const showMinimize = !isMobile && store.showQuestionnaire && !store.isQuestionnaireMinimized
  const showExpand = !isMobile && store.showQuestionnaire && store.isQuestionnaireMinimized

  return (
    <div className={cx(Styles.bar, Styles.generating)}>
      <div className={Styles.barContent}>
        <div className={Styles.statusSection}>
          <div className={Styles.spinnerWrap}>
            <img src={spinnerImg} alt="" className={Styles.spinnerImg} />
          </div>
          <span className={Styles.statusLabelWrapper}>
            <span className={Styles.statusLabel}>Generating{!isMobile && ' your content'}</span>
            {!isMobile && <span className={Styles.animatedDots} />}
          </span>
        </div>
        <div className={Styles.rightSection}>
          <div className={Styles.counters}>
            {showCounter && (
              <span className={Styles.countText}>
                {!isMobile && 'Generated '}
                {store.completedCount}/{displayTotal} {pluralize('post', displayTotal)}
              </span>
            )}
            <span className={Styles.timerText}>{elapsed}</span>
          </div>
          {showMinimize && (
            <button className={Styles.minimizeButton} onClick={() => store.setQuestionnaireMinimized(true)}>
              Minimize
            </button>
          )}
          {showExpand && (
            <button className={Styles.minimizeButton} onClick={() => store.setQuestionnaireMinimized(false)}>
              Expand
            </button>
          )}
        </div>
      </div>
    </div>
  )
})

const FinishedBar = observer(() => {
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()

  const handleGoToContent = () => {
    history.push(generatePath(SCHEDULED_POSTS_PATH, { workspaceId }))
  }

  return (
    <div className={cx(Styles.bar, Styles.finished)}>
      <div className={Styles.finishedContent}>
        <span className={Styles.finishedText}>All content is finished generating.</span>
        <button className={cx(Styles.actionButton, Styles.finishedActionButton)} onClick={handleGoToContent}>
          Go to Content
        </button>
      </div>
    </div>
  )
})

export function useElapsedTime(startedAt: number | null) {
  const [, forceUpdate] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (startedAt !== null) {
      intervalRef.current = setInterval(() => forceUpdate((n) => n + 1), 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [startedAt])

  if (startedAt === null) return '0:00'

  const totalSeconds = Math.floor((Date.now() - startedAt) / 1000)
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
